import React from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { AlertCircle, WifiOff, Info, HelpCircle } from 'lucide-react';

type ErrorCode =
  | 'offline'
  | 'timeout'
  | 'storage_unavailable'
  | 'unauthorized'
  | 'forbidden'
  | 'missing_scopes'
  | 'bad_request'
  | 'schema_mismatch'
  | 'snapshot_unavailable'
  | 'conflict'
  | 'rate_limited'
  | 'quota_exceeded'
  | 'system_down'
  | 'unexpected'
  | 'unknown';

type ErrorInfo = {
  title: string;
  description: string;
  icon: React.ReactNode;
  primaryAction: 'retry' | 'go_back' | 'go_home' | 'open_settings';
  primaryActionLabel: string;
};

const errorMap: Record<ErrorCode, ErrorInfo> = {
  offline: {
    title: "You're offline",
    description: "We couldn’t reach the app. Check your connection and try again.",
    icon: <WifiOff className="h-8 w-8 text-amber-500" />,
    primaryAction: 'retry',
    primaryActionLabel: 'Try Again',
  },
  timeout: {
    title: 'This took too long',
    description: 'The operation didn’t finish in time. It might be a temporary slowdown.',
    icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
    primaryAction: 'retry',
    primaryActionLabel: 'Retry',
  },
  storage_unavailable: {
      title: "Can't access local storage",
      description: "Your browser has blocked local storage or it's full. This app needs storage to work correctly.",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
      primaryAction: 'open_settings',
      primaryActionLabel: 'Open Settings',
  },
  unauthorized: {
    title: 'Sign-in required',
    description: 'You don’t seem to be signed in or your session expired.',
    icon: <AlertCircle className="h-8 w-8 text-red-500" />,
    primaryAction: 'retry',
    primaryActionLabel: 'Retry Sign-in',
  },
  forbidden: {
      title: "You don't have access",
      description: "Your account lacks permission for this action.",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
      primaryAction: 'go_back',
      primaryActionLabel: 'Go Back',
  },
  missing_scopes: {
      title: 'More permissions needed',
      description: "Your connected provider account is missing required permissions for this action.",
      icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
      primaryAction: 'open_settings',
      primaryActionLabel: 'Open Settings',
  },
  bad_request: {
    title: 'Bad Request',
    description: 'A required field was missing or invalid in the last request.',
    icon: <Info className="h-8 w-8 text-blue-500" />,
    primaryAction: 'go_back',
    primaryActionLabel: 'Go Back',
  },
  schema_mismatch: {
    title: 'App data needs an update',
    description: "Your local data uses an older format. We'll try to migrate it.",
    icon: <Info className="h-8 w-8 text-blue-500" />,
    primaryAction: 'retry',
    primaryActionLabel: 'Retry Migration',
  },
  snapshot_unavailable: {
      title: "Couldn't load your workspace",
      description: "No saved data was found and we couldn't load live data.",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
      primaryAction: 'retry',
      primaryActionLabel: 'Try Again',
  },
  conflict: {
      title: "Change conflict detected",
      description: "Your recent changes conflicted with newer data from another session.",
      icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
      primaryAction: 'go_back',
      primaryActionLabel: 'Go Back to Review',
  },
  rate_limited: {
      title: 'Too many requests',
      description: "We've hit a temporary limit. Please wait a moment and retry.",
      icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
      primaryAction: 'retry',
      primaryActionLabel: 'Retry',
  },
  quota_exceeded: {
      title: 'Limit reached',
      description: "This action exceeds your configured limits for this resource.",
      icon: <AlertCircle className="h-8 w-8 text-amber-500" />,
      primaryAction: 'open_settings',
      primaryActionLabel: 'Open Settings',
  },
  system_down: {
      title: 'Service is temporarily unavailable',
      description: "A dependency is experiencing issues. It usually clears up shortly.",
      icon: <AlertCircle className="h-8 w-8 text-red-500" />,
      primaryAction: 'retry',
      primaryActionLabel: 'Try Again',
  },
  unexpected: {
    title: 'An unexpected error occurred',
    description: 'Something went wrong. Your data should be safe. Please try again.',
    icon: <AlertCircle className="h-8 w-8 text-red-500" />,
    primaryAction: 'retry',
    primaryActionLabel: 'Try Again',
  },
  unknown: {
    title: 'An unknown error occurred',
    description: "We're not sure what happened. Please try going back or starting over.",
    icon: <HelpCircle className="h-8 w-8 text-gray-500" />,
    primaryAction: 'go_home',
    primaryActionLabel: 'Go Home',
  },
};

export default function ErrorPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const code = (searchParams.get('code') as ErrorCode) || 'unknown';
  const reason = searchParams.get('reason');
  const returnTo = searchParams.get('returnTo');

  const errorInfo = errorMap[code] || errorMap.unknown;

  const handlePrimaryAction = () => {
    switch (errorInfo.primaryAction) {
      case 'retry':
        window.location.reload();
        break;
      case 'go_back':
        if (returnTo) {
          navigate(returnTo);
        } else {
          navigate(-1); // Go back one step in history
        }
        break;
      case 'open_settings':
        navigate('/pages/settings/');
        break;
      case 'go_home':
      default:
        // navigate('/pages/workspace/');
        break;
    }
  };

  const diagnostics = Object.fromEntries(searchParams.entries());

  const copyDiagnostics = () => {
    navigator.clipboard.writeText(JSON.stringify(diagnostics, null, 2));
    // In a real app, you might show a "Copied!" toast here.
  }

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4">{errorInfo.icon}</div>
          <CardTitle className="text-2xl">{errorInfo.title}</CardTitle>
          <CardDescription>{errorInfo.description}</CardDescription>
        </CardHeader>
        <CardContent>
          {reason && (
            <div className="bg-muted p-3 rounded-md text-sm text-muted-foreground font-mono">
              <p><strong>Reason:</strong> {reason}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <div className="flex justify-center gap-2 w-full">
            <Button onClick={handlePrimaryAction}>{errorInfo.primaryActionLabel}</Button>
            {errorInfo.primaryAction !== 'go_home' && <Button variant="outline" onClick={() => navigate('/pages/workspace/')}>Go Home</Button>}
            {errorInfo.primaryAction !== 'go_back' && <Button variant="ghost" onClick={() => navigate(-1)}>Go Back</Button>}
          </div>

          <Accordion type="single" collapsible className="w-full">
            <AccordionItem value="diagnostics">
              <AccordionTrigger>Show Diagnostics</AccordionTrigger>
              <AccordionContent>
                <div className="bg-muted p-3 rounded-md text-xs text-muted-foreground font-mono break-all max-h-48 overflow-auto">
                  <pre>{JSON.stringify(diagnostics, null, 2)}</pre>
                </div>
                <Button variant="link" size="sm" className="mt-2 px-0" onClick={copyDiagnostics}>Copy Diagnostics</Button>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </CardFooter>
      </Card>
    </div>
  );
}